import { useMemo, useState } from 'react';
import {
  PERMISSION_LABELS,
  CATALOGUE,
  type ActionKey,
  type FieldAccess,
  type ResourceName,
  type RoleWire,
  type Scope,
} from '@forecastify/shared';
import { Button, Dialog, Icon, Select, TextInput } from '@forecastify/ui';
import { useI18n, type MessageKey } from '../../i18n';
import { ScopeSelect } from './scope-select';
import { SettingsTable } from '../settings/settings-table';
import { SettingsPage } from '../settings/settings-page';
import { Field } from '../card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Jogosultságok — the mockup's screen, redrawn for scopes and field rules.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `main.jsx` 4284–4402 for the logic, `markup.html` for the drawing. The layout is the mockup's,
 * measured out of it rather than eyeballed: `max-w-[980px]`, `lg:grid-cols-[280px_minmax(0,1fr)]`,
 * `gap-3`, level rows at 12.5px with a 10.5px mono code, group headers at 10.5px uppercase on
 * `bg-muted/40`.
 *
 * ⚠️ **What is DIFFERENT, and why, in one place:**
 *
 *  1. **A scope selector, not a checkbox** — `decisions.md` R-4. The mockup's own catalogue had to
 *     invent `worklog.own`, `worklog.team`, `worklog.all` and `worklog.edit.any` to say what one
 *     action at four scopes says; those four keys are one action here, and the row shows the ladder.
 *  2. **A field-rule section**, which the mockup has nowhere at all — because it hides nothing and
 *     makes nothing read-only, and the product does both.
 *  3. **`project.create` and `people.deactivate` appear**, flagged as additions in `decisions.md`.
 *     They are in the catalogue and therefore in this screen; a screen that quietly omitted them
 *     would be the one place somebody looks to find out what exists.
 *  4. **Only Owner and Admin are locked.** The mockup marks `builtin` on those two, and the seed
 *     agrees — so Projektmenedzser, Munkatárs and Alvállalkozó are editable starting points. The
 *     intuitive reading would have locked all five (`hard-won.md` C5-5).
 *
 * ⚠️ **`props` rather than a fetch, for now.** The screen takes its data as props and the route
 * passes the empty state; the fetch belongs with the API client's auth handling, which C6 owns. What
 * matters is that *this file* already renders the real wire shape (`RoleWire`) rather than a
 * convenient local one — swapping a loader in front of it cannot change the markup.
 */

/* ── mapping the catalogue to the mockup's groups ────────────────────────────────────────── */

/**
 * ⚠️ **The group order and titles are the MOCKUP's, and the mapping is explicit.**
 *
 * `CATALOGUE` is keyed by resource; the mockup groups by subject, and the two are not the same
 * shape — it puts projects and clients in one group, and `client.manage` belongs to the `project`
 * resource. Deriving the groups from the resource names would silently reorder the screen and lose
 * *"Projektek és ügyfelek"*, which is the title a Hungarian user has already read.
 */
const GROUPS: readonly {
  readonly title: MessageKey;
  readonly resources: readonly ResourceName[];
}[] = [
  { title: 'permissions.group.task', resources: ['task'] },
  { title: 'permissions.group.worklog', resources: ['worklog'] },
  { title: 'permissions.group.project', resources: ['project'] },
  /**
   * ⚠️⚠️ **`contract` WAS IN THE CATALOGUE AND IN NO GROUP, SO ITS THREE ACTIONS COULD NOT BE
   * GRANTED FROM THIS SCREEN AT ALL.** C24 added them and `0051` gave them to the built-in roles;
   * this list was never told, and a resource missing from it renders nothing. Found while adding
   * `leave` and fixed in the same line of work, because a hole somebody has to remember is a hole.
   */
  { title: 'permissions.group.contract', resources: ['contract'] },
  { title: 'permissions.group.people', resources: ['people'] },
  /* C47 · beside people, because it is about their absences. */
  { title: 'permissions.group.leave', resources: ['leave'] },
  { title: 'permissions.group.report', resources: ['report'] },
  /* 22 Oct 2026 · the workspace menu's rows and the dashboards, a permission each. */
  { title: 'permissions.group.menu', resources: ['menu'] },
  { title: 'permissions.group.dashboard', resources: ['dashboard'] },
  { title: 'permissions.group.settings', resources: ['settings'] },
];

/** The actions of one resource, in the order the catalogue declares them. */
function actionsOf(resource: ResourceName): readonly ActionKey[] {
  return Object.values(CATALOGUE[resource].actions);
}

/**
 * A copy without one key.
 *
 * ⚠️ **Not `delete`, which the lint refuses on a computed key — and it is right to.** `delete`
 * on a shape TypeScript believes is total leaves the type claiming a property the object no longer
 * has, and the draft here is exactly such a shape. Rebuilding is also what React wants: a new
 * object rather than a mutated one.
 */
function without<T extends object>(record: T, key: string): T {
  return Object.fromEntries(Object.entries(record).filter(([name]) => name !== key)) as T;
}

/* ── the draft ───────────────────────────────────────────────────────────────────────────── */

interface Draft {
  readonly grants: Readonly<Partial<Record<ActionKey, Scope>>>;
  readonly fieldRules: Readonly<Record<string, FieldAccess>>;
}

function draftOf(role: RoleWire): Draft {
  return {
    grants: role.grants,
    fieldRules: Object.fromEntries(
      role.fieldRules.map((rule) => [`${rule.resource}.${rule.field}`, rule.access]),
    ),
  };
}

/* ── the screen ──────────────────────────────────────────────────────────────────────────── */

export interface PermissionsScreenProps {
  /** Until the first answer: skeleton rows, and no *"this level does not exist"*. */
  readonly loading?: boolean;
  readonly roles?: readonly RoleWire[];
  /**
   * ⚠️⚠️ **THE STEP, AND IT IS AN ADDRESS — item 62.** `undefined` is the list; a role id is the
   * editor. The route owns it (`/settings/permissions/:roleId`) rather than the screen, so a level
   * somebody is arguing about is a link, the browser's Back works, and the render gate can see the
   * second step at all.
   */
  readonly selectedId?: string | undefined;
  readonly onSelect?: (roleId: string) => void;
  readonly onBack?: () => void;
  readonly onSave?: (roleId: string, draft: Draft, version: number) => Promise<void>;
  /**
   * ⚠️⚠️ **CREATING A LEVEL, WHICH THE BUTTON PROMISED AND NOTHING DELIVERED.** The plus sat in the
   * header with no `onClick` from the day it was drawn: the API had `GET roles` and `PUT roles/:id`
   * and no create at all. Both halves exist now.
   *
   * ⚠️ **It resolves to the NEW LEVEL'S ID**, because the useful thing to do next is open it — a
   * level created empty is a level with nothing in it, and the screen that fixes that is the one
   * behind this list.
   */
  readonly onCreate?: (name: string, copyFromRoleId: string | null) => Promise<string | null>;
  /** ⚠️ Absent for a level somebody holds or a built-in one — the row says which. */
  readonly onDelete?: (roleId: string) => Promise<void>;
  /** Already translated. A refusal from either verb, shown in the dialog that caused it. */
  readonly serverError?: string | null;
}

export function PermissionsScreen({
  loading = false,
  roles = [],
  selectedId,
  onSelect,
  onBack,
  onSave,
  onCreate,
  onDelete,
  serverError = null,
}: PermissionsScreenProps) {
  const { t, locale } = useI18n();
  /* ⚠️ `null` is closed. A level being created has a name and a source, both empty to begin with. */
  const [draftLevel, setDraftLevel] = useState<{ name: string; copyFrom: string } | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ TWO STEPS NOW, WHERE THIS WAS A LIST BESIDE A PERMANENT EDITOR.
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The feedback: *"the first list page, and then the actual permissions — a two step settings
   * page."* That reading is the better one for what this screen holds. A level carries thirty-one
   * actions in six groups; beside a 280px list the editor had ~700px to draw them in, so every
   * scope selector was squeezed and the groups scrolled inside their own column. On its own step
   * it has the page.
   *
   * ⚠️ **No default selection any more.** Landing on the first editable role was right while the
   * editor was always on screen; it is wrong when arriving on this address means choosing one.
   */
  const selected = roles.find((role) => role.id === selectedId);
  /**
   * ⚠️⚠️ **THE STEP IS THE ADDRESS, NOT "DID THE ROLE ARRIVE".** Deriving it from `selected` meant
   * the list was drawn for the whole of the round trip that loads the roles, and for ever on an id
   * that names none — so a bookmarked level flashed the list and an edited-away role silently
   * became the list, with the address still claiming otherwise. `routes.test.tsx` caught the first
   * of those. `selectedId` is known synchronously, because it came from the URL.
   */
  const onLevel = selectedId !== undefined;

  /**
   * ⚠️ **The editor holds its OWN draft, keyed by role id.**
   *
   * Deriving the rendered value from the loaded role would make an in-progress edit unrepresentable
   * — the same defect as a tag editor that renders `join(', ')` and parses `split(',')`, where a
   * comma becomes impossible to type and every test passes because they all set the value
   * programmatically. Keying by id resets the draft when somebody selects another level, which is
   * the behaviour they expect and the one a single `useState` would get wrong.
   */
  const [drafts, setDrafts] = useState<Readonly<Record<string, Draft>>>({});
  const [saving, setSaving] = useState(false);

  const draft: Draft | undefined = useMemo(() => {
    if (selected === undefined) return undefined;
    return drafts[selected.id] ?? draftOf(selected);
  }, [drafts, selected]);

  const dirty =
    selected !== undefined &&
    draft !== undefined &&
    JSON.stringify(draft) !== JSON.stringify(draftOf(selected));

  const grantedCount = draft === undefined ? 0 : Object.keys(draft.grants).length;

  const setGrant = (action: ActionKey, scope: Scope): void => {
    if (selected === undefined || draft === undefined) return;
    /* ⚠️ `none` is DROPPED, not stored — the same rule the service applies. Two representations
     * of one state is how a count of grants comes to disagree with a read of them. */
    const grants =
      scope === 'none' ? without(draft.grants, action) : { ...draft.grants, [action]: scope };
    setDrafts((current) => ({ ...current, [selected.id]: { ...draft, grants } }));
  };

  /* ⚠️ `setFieldRule` and the `fieldKeys` list went with the field-rules section — item 62. The
     DRAFT still carries whatever was loaded, and `permissions-route.tsx` still sends it, so a
     stored rule round-trips untouched through a save that no longer draws it. */

  return (
    /**
     * ⚠️⚠️ **A MARKER PER STEP, BECAUSE THE ROUTE GATE REQUIRES ONE PER ADDRESS.** One component
     * renders both steps, but `routes.test.tsx` asserts every route has a DISTINCT marker — and it
     * is right to: two addresses sharing a marker is how a route silently resolves to its
     * neighbour and the check still passes. The step is the screen here, so it names itself.
     */
    <div
      data-screen={onLevel ? 'permission-level' : 'permissions'}
      className="min-h-0 flex-1 overflow-y-auto"
    >
      {/**
       * ⚠️⚠️ **THE 980px CAP IS GONE — round 5 item 2, second pass.** It was measured out of
       * the mockup and it was right for a page of its own; this is a settings page, and the rule
       * the round settled is that **every settings table spans its section**, as Ünnepnapok does.
       * A capped table beside eleven full-width ones reads as the odd one out rather than as the
       * considered one. Reported again on 20 Sep, naming this page.
       */}
      <SettingsPage
        title={onLevel ? (selected?.name ?? t('permissions.levels.title')) : t('nav.permissions')}
        action={
          onLevel || onCreate === undefined
            ? undefined
            : {
                label: t('permissions.levels.new'),
                icon: 'plus',
                onSelect: () => {
                  setDraftLevel({ name: '', copyFrom: '' });
                },
              }
        }
      >
        <div className="flex flex-col gap-3">
          {/**
           * ┌───────────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE CTA SITS ABOVE THE TABLE AND IS PURPLE — the settings rule, 15 Sep.│
           * └───────────────────────────────────────────────────────────────────────────┘
           *
           * *"New level is in one line with the title, not purple; we need it above the table, and
           * purple."* Work schedules is the shape every workspace setting now follows: the primary
           * action sits on the row above the table, in the accent, outside the card it acts on.
           *
           * ⚠️⚠️ **AND IT CONTRADICTS ITEM 25, WHICH IS NOT A MISTAKE IN EITHER.** The list screens —
           * tasks, projects, people, clients, contracts — put their create in the TOOLBAR as an
           * outline button, on 15 Sep, because a list has a toolbar and the create belongs beside the
           * search. A settings screen has no toolbar and no search: it has a title, sometimes a tab
           * strip, and a table. The two rules are about two different furnitures, and applying either
           * to the other is what made the product look assembled from parts.
           */}

          {/* ── step one · the levels ─────────────────────────────────────────────── */}
          {!onLevel && (
            <SettingsTable
              grid="minmax(0,1fr) 36px"
              minWidth={0}
              columns={[{}, {}]}
              rowHeight={46}
              groups={[
                {
                  rows: roles.map((role) => ({
                    id: role.id,
                    onOpen: () => {
                      onSelect?.(role.id);
                    },
                    cells: [
                      /* ⚠️ A name and nothing else — item 62 took the code, the *built-in* badge,
                       the description and *"14 of 31 granted · 6 members"* off this row. It is a
                       thing to pick rather than a record to read. */
                      { kind: 'name', text: role.name },
                      onDelete === undefined
                        ? { kind: 'blank' }
                        : {
                            kind: 'kebab',
                            ariaLabel: t('common.more'),
                            items: [
                              {
                                label: t('permissions.levels.open'),
                                icon: 'arrow-right',
                                onSelect: () => {
                                  onSelect?.(role.id);
                                },
                              },
                              { type: 'separator' },
                              {
                                /**
                                 * ⚠️⚠️ **DISABLED WITH ITS REASON IN THE `description`, WHICH A MENU
                                 * CAN DO AND A DISABLED BUTTON CANNOT.** Two reasons a delete cannot
                                 * run: a built-in level may not go (`0009` — a workspace whose last
                                 * full-access level is gone cannot be administered by anybody), and
                                 * a level somebody HOLDS may not go either, because it would strip
                                 * permissions from real people who would find out by being unable to
                                 * work.
                                 */
                                label: t('permissions.levels.delete'),
                                icon: 'trash',
                                destructive: true,
                                disabled: busy || role.builtin || role.memberCount > 0,
                                description: role.builtin
                                  ? t('permissions.levels.deleteBuiltin')
                                  : role.memberCount > 0
                                    ? t('permissions.levels.deleteHeld', {
                                        count: role.memberCount,
                                      })
                                    : undefined,
                                onSelect: () => {
                                  if (role.builtin || role.memberCount > 0) return;
                                  void (async () => {
                                    setBusy(true);
                                    await onDelete(role.id);
                                    setBusy(false);
                                  })();
                                },
                              },
                            ],
                          },
                    ],
                  })),
                },
              ]}
              loading={loading}
              empty={{
                icon: 'shield-check',
                title: t('permissions.levels.title'),
                body: t('permissions.levels.emptyBody'),
              }}
            />
          )}

          {/**
           * ⚠️⚠️ **AN ADDRESS THAT NAMES NO LEVEL SAYS SO, RATHER THAN SILENTLY BECOMING THE LIST.**
           * A bookmarked level that has since been deleted, or a mistyped id, used to render step
           * one with the address still claiming step two — the *"a route silently fell through"*
           * failure CLAUDE.md §4 names, one level down.
           */}
          {onLevel && selected === undefined && !loading && (
            <section className="bg-card text-card-foreground rounded-xl border p-6 text-center shadow-sm">
              <p className="text-[13px]">{t('permissions.level.missing')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => {
                  onBack?.();
                }}
              >
                {t('permissions.levels.title')}
              </Button>
            </section>
          )}

          {selected !== undefined && (
            <section className="bg-card text-card-foreground min-w-0 rounded-xl border shadow-sm">
              <header className="flex items-start gap-3 border-b px-4 py-3">
                {/**
                 * ⚠️⚠️ **A BACK CONTROL, BECAUSE THE LIST IS NO LONGER BESIDE IT.** In the two-column
                 * layout the way out was looking left. On a step it has to be a control, and it has to
                 * be the first thing in the header — `person-screen.tsx` puts its own there for the
                 * same reason.
                 */}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="-ml-1.5 shrink-0"
                  aria-label={t('permissions.levels.title')}
                  onClick={() => {
                    onBack?.();
                  }}
                >
                  <Icon name="chevron-left" size={16} />
                </Button>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[13px] font-medium">
                    {t('permissions.detail.title', { name: selected.name })}
                  </h2>
                  <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
                    {selected.builtin
                      ? ''
                      : t('permissions.detail.note', {
                          granted: grantedCount,
                          members: selected.memberCount,
                        })}
                  </p>
                </div>

                {/* ⚠️ The save control appears only when there is something to save, and it names the
                unsaved state in words rather than only by being enabled. */}
                {dirty && (
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-muted-foreground text-[11.5px]">
                      {t('permissions.unsaved')}
                    </span>
                    <Button
                      size="sm"
                      disabled={saving}
                      onClick={() => {
                        /* ⚠️ No `selected`/`draft` guard: `dirty` above already narrows both to
                         * defined, and the lint reports the extra checks as dead. Writing them anyway
                         * would be a guard that reads as protection and provides none. */
                        if (onSave === undefined) return;
                        setSaving(true);
                        void onSave(selected.id, draft, selected.version).finally(() => {
                          setSaving(false);
                        });
                      }}
                    >
                      {saving ? t('permissions.saving') : t('permissions.save')}
                    </Button>
                  </div>
                )}
              </header>

              {GROUPS.map((group) => (
                <div key={group.title} className="border-b last:border-0">
                  <h3 className="text-muted-foreground bg-muted/40 px-4 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
                    {t(group.title)}
                  </h3>

                  {group.resources.flatMap((resource) =>
                    actionsOf(resource).map((action) => {
                      const scope = draft?.grants[action] ?? 'none';
                      const label = PERMISSION_LABELS[action][locale];
                      return (
                        <div
                          key={action}
                          className="flex items-center gap-2.5 border-b px-4 py-2 last:border-0"
                        >
                          <span className="min-w-0 flex-1 text-[12.5px]">{label}</span>
                          {/**
                           * ⚠️⚠️ **THE ACTION KEY IS GONE — item 62, and *"permission nodes"* is what
                           * it called this.** Every row printed `task.view`, `task.create`,
                           * `task.assign` beside its own label: our internal identifier, in English,
                           * on a Hungarian screen, saying nothing the label had not already said. It
                           * was asked for twice.
                           *
                           * ⚠️ **The key still exists and is still the truth** — `ACTION_LABEL` maps
                           * it to the words, `ScopeSelect` takes it, and the wire sends it. What is
                           * gone is showing it to somebody choosing a permission.
                           */}
                          <ScopeSelect
                            action={action}
                            actionLabel={label}
                            value={scope}
                            disabled={selected.builtin}
                            onChange={(next) => {
                              setGrant(action, next);
                            }}
                          />
                        </div>
                      );
                    }),
                  )}
                </div>
              ))}

              {/*
            ⚠️⚠️ **THE FIELD-RULES SECTION IS GONE — item 62's *"permission nodes"*.** It listed
            raw column keys — `person.phone`, `contract.value` — each with a hidden/read/write
            picker, under a heading the mockup draws nowhere. Its own note said as much when it was
            built.

            ⚠️⚠️ **THE RULES THEMSELVES ARE UNTOUCHED AND STILL ENFORCED.** `fieldRules` is part of
            a role on the wire, the serializer and the request validator both apply it centrally
            (CLAUDE.md's permissions model), and `roles.service.ts` keeps whatever is stored: this
            screen sends the grants it edits and does not clear what it no longer draws. What is
            gone is the only way to CHANGE one from the UI, which is why `debt.md` carries the row —
            a workspace that needs a field hidden now needs an operator.
          */}
            </section>
          )}
        </div>

        {/**
         * ┌───────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ A NEW LEVEL — a name, and optionally somebody to copy.       │
         * └───────────────────────────────────────────────────────────────────┘
         *
         * ⚠️ **Two fields, and neither of them is a grant.** A level is created and then edited; the
         * editor is the other half of this screen and it opens the moment this closes. A dialog that
         * tried to set twenty-five actions across four scopes would be that editor, badly.
         *
         * ⚠️ **The copy source is the reason this is not just a name box.** *"Like Member, plus
         * contracts"* is how a workspace actually describes a new level, and starting from nothing
         * means ticking everything back on before the real change begins.
         */}
      </SettingsPage>

      <Dialog
        open={draftLevel !== null}
        onOpenChange={(next) => {
          if (!next) setDraftLevel(null);
        }}
        title={t('permissions.levels.new')}
        description={t('permissions.levels.newHint')}
        closeLabel={t('common.close')}
        width={480}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setDraftLevel(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={busy || draftLevel === null || draftLevel.name.trim() === ''}
              onClick={() => {
                if (draftLevel === null) return;
                void (async () => {
                  setBusy(true);
                  const created = await onCreate?.(
                    draftLevel.name.trim(),
                    draftLevel.copyFrom === '' ? null : draftLevel.copyFrom,
                  );
                  setBusy(false);
                  /* ⚠️ Stays open on a refusal, with the typed name still in it — the dialog's own
                     error line says why. Closing would discard the form and leave the message
                     beside a dialog that is no longer there. */
                  if (created === null || created === undefined) return;
                  setDraftLevel(null);
                  onSelect?.(created);
                })();
              }}
            >
              {t('permissions.levels.create')}
            </Button>
          </>
        }
      >
        {draftLevel === null ? null : (
          <div className="flex flex-col gap-3">
            <Field label={t('permissions.levels.name')} required>
              <TextInput
                value={draftLevel.name}
                placeholder={t('permissions.levels.namePlaceholder')}
                onChange={(event) => {
                  setDraftLevel({ ...draftLevel, name: event.target.value });
                }}
              />
            </Field>

            <Field label={t('permissions.levels.copyFrom')} note={t('permissions.levels.copyNote')}>
              <Select
                aria-label={t('permissions.levels.copyFrom')}
                value={draftLevel.copyFrom}
                onChange={(event) => {
                  setDraftLevel({ ...draftLevel, copyFrom: event.target.value });
                }}
                options={[
                  { value: '', label: t('permissions.levels.copyNone') },
                  ...roles.map((role) => ({ value: role.id, label: role.name })),
                ]}
              />
            </Field>

            {serverError === null ? null : (
              <p className="text-destructive text-[12px]">{serverError}</p>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
